import React, { useEffect, useRef, useState } from "react";
import { MarkerClusterer } from "@googlemaps/markerclusterer";
import { loadGoogleMaps } from "../lib/googleMaps";
import { categoryFor } from "../lib/categories";
import { CATEGORY_PATHS, CATEGORY_STROKE } from "../lib/categoryIcons";
import { faviconFor } from "../lib/api";

const GOOGLE_KEY = process.env.REACT_APP_GOOGLE_MAPS_KEY;

// Strict outer restriction (map cannot pan past North America).
const NA_RESTRICTION = { north: 72, south: 15, east: -52, west: -172 };
// Initial view — continental USA + Canada (no Pacific gap).
const DEFAULT_VIEW_BOUNDS = { north: 56, south: 24, east: -66, west: -128 };

// Light "clean" map style similar to CARTO Light.
const LIGHT_STYLE = [
    { elementType: "geometry", stylers: [{ color: "#f5f5f5" }] },
    { elementType: "labels.icon", stylers: [{ visibility: "off" }] },
    { elementType: "labels.text.fill", stylers: [{ color: "#5c6470" }] },
    { elementType: "labels.text.stroke", stylers: [{ color: "#ffffff" }] },
    { featureType: "administrative.country", elementType: "geometry.stroke", stylers: [{ color: "#c9cdd2" }] },
    { featureType: "administrative.province", elementType: "geometry.stroke", stylers: [{ color: "#d8dbdf" }] },
    { featureType: "poi", stylers: [{ visibility: "off" }] },
    { featureType: "poi.park", elementType: "geometry", stylers: [{ color: "#e9ece2" }] },
    { featureType: "road", elementType: "geometry", stylers: [{ color: "#ffffff" }] },
    { featureType: "road", elementType: "labels", stylers: [{ visibility: "off" }] },
    { featureType: "road.highway", elementType: "geometry", stylers: [{ color: "#f0e6dc" }] },
    { featureType: "transit", stylers: [{ visibility: "off" }] },
    { featureType: "water", elementType: "geometry", stylers: [{ color: "#d6e6ee" }] },
    { featureType: "water", elementType: "labels.text.fill", stylers: [{ color: "#8fa3ad" }] },
];

// ---------- Pin icons (cached; only ~10 unique SVGs used for 1800+ markers). ----------
const _pinCache = {};

function categoryPinSvg(catId) {
    const stroke = CATEGORY_STROKE[catId] || CATEGORY_STROKE._default;
    const path = CATEGORY_PATHS[catId] || CATEGORY_PATHS._default;
    return `<svg xmlns="http://www.w3.org/2000/svg" width="46" height="56" viewBox="0 0 46 56">
<defs><filter id="d" x="-30%" y="-20%" width="160%" height="160%">
<feDropShadow dx="0" dy="3" stdDeviation="2.5" flood-color="${stroke}" flood-opacity="0.35"/></filter></defs>
<g filter="url(#d)">
<polygon points="23,54 15,44 31,44" fill="${stroke}"/>
<circle cx="23" cy="21" r="19" fill="#ffffff" stroke="${stroke}" stroke-width="3"/>
<g transform="translate(11,9)" stroke="${stroke}" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" fill="none">${path}</g>
</g></svg>`;
}

// Company-logo pin: same teardrop frame as the category pin, but with the
// company's favicon rendered inside the white circle. The category icon is
// drawn first as a fallback layer so if the remote favicon fails to load or is
// still fetching, the pin still shows a recognisable icon (no blank pins).
function logoPinSvg(catId, logoUrl) {
    const stroke = CATEGORY_STROKE[catId] || CATEGORY_STROKE._default;
    const path = CATEGORY_PATHS[catId] || CATEGORY_PATHS._default;
    // Escape the URL for safe embedding inside the SVG data URL.
    const safeUrl = (logoUrl || "").replace(/&/g, "&amp;").replace(/"/g, "&quot;");
    return `<svg xmlns="http://www.w3.org/2000/svg" width="46" height="56" viewBox="0 0 46 56">
<defs>
<filter id="d" x="-30%" y="-20%" width="160%" height="160%">
<feDropShadow dx="0" dy="3" stdDeviation="2.5" flood-color="${stroke}" flood-opacity="0.35"/>
</filter>
<clipPath id="lc"><circle cx="23" cy="21" r="14"/></clipPath>
</defs>
<g filter="url(#d)">
<polygon points="23,54 15,44 31,44" fill="${stroke}"/>
<circle cx="23" cy="21" r="19" fill="#ffffff" stroke="${stroke}" stroke-width="3"/>
<g transform="translate(11,9)" stroke="${stroke}" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" fill="none" opacity="0.35">${path}</g>
<image href="${safeUrl}" x="9" y="7" width="28" height="28" clip-path="url(#lc)" preserveAspectRatio="xMidYMid slice"/>
</g></svg>`;
}

// Featured employer pin — brand orange, with a star for extra visibility.
function employerPinSvg() {
    return `<svg xmlns="http://www.w3.org/2000/svg" width="54" height="64" viewBox="0 0 54 64">
<defs><filter id="d2" x="-30%" y="-20%" width="160%" height="160%">
<feDropShadow dx="0" dy="4" stdDeviation="3" flood-color="#a5340a" flood-opacity="0.55"/></filter>
<radialGradient id="og" cx="50%" cy="30%" r="70%">
<stop offset="0%" stop-color="#ffb488"/><stop offset="55%" stop-color="#f47c3c"/><stop offset="100%" stop-color="#d94b08"/>
</radialGradient></defs>
<g filter="url(#d2)">
<polygon points="27,62 18,50 36,50" fill="#d94b08"/>
<circle cx="27" cy="25" r="22" fill="url(#og)" stroke="#ffffff" stroke-width="3"/>
<polygon points="27,12 30.2,20.6 39.4,20.9 32.1,26.5 34.6,35.4 27,30.4 19.4,35.4 21.9,26.5 14.6,20.9 23.8,20.6"
 fill="#ffffff" opacity="0.98"/>
</g></svg>`;
}

// Golden featured pin — top-tier paid/admin-boosted employer job. More prominent than regular employer pins.
// Includes a subtle SVG SMIL shimmer (opacity + scale pulse on the outer halo)
// so premium employers stand out even before the user hovers.
function featuredGoldPinSvg() {
    return `<svg xmlns="http://www.w3.org/2000/svg" width="58" height="70" viewBox="0 0 58 70">
<defs><filter id="d3" x="-30%" y="-20%" width="160%" height="160%">
<feDropShadow dx="0" dy="5" stdDeviation="3.5" flood-color="#8a6a06" flood-opacity="0.6"/></filter>
<radialGradient id="gg" cx="50%" cy="25%" r="75%">
<stop offset="0%" stop-color="#fff3b8"/><stop offset="45%" stop-color="#f5c542"/><stop offset="100%" stop-color="#b8890c"/>
</radialGradient>
<linearGradient id="gring" x1="0%" y1="0%" x2="0%" y2="100%">
<stop offset="0%" stop-color="#ffffff"/><stop offset="100%" stop-color="#fff2c4"/>
</linearGradient>
<linearGradient id="gshine" x1="0%" y1="0%" x2="100%" y2="0%">
<stop offset="0%" stop-color="#fff9dd" stop-opacity="0"/>
<stop offset="50%" stop-color="#ffffff" stop-opacity="0.85"/>
<stop offset="100%" stop-color="#fff9dd" stop-opacity="0"/>
</linearGradient>
<clipPath id="gclip"><circle cx="29" cy="27" r="24"/></clipPath></defs>
<g filter="url(#d3)">
<polygon points="29,68 19,55 39,55" fill="#b8890c"/>
<!-- Pulsing outer halo -->
<circle cx="29" cy="27" r="26" fill="none" stroke="#f5c542" stroke-width="2" opacity="0.55">
  <animate attributeName="r" values="24;30;24" dur="2.4s" repeatCount="indefinite"/>
  <animate attributeName="opacity" values="0.6;0;0.6" dur="2.4s" repeatCount="indefinite"/>
</circle>
<circle cx="29" cy="27" r="24" fill="url(#gg)" stroke="url(#gring)" stroke-width="3.5"/>
<!-- Shimmer sweep across the pin face -->
<g clip-path="url(#gclip)">
  <rect x="-30" y="3" width="20" height="48" fill="url(#gshine)" opacity="0.9" transform="skewX(-18)">
    <animate attributeName="x" values="-30;70;70" keyTimes="0;0.55;1" dur="3.2s" repeatCount="indefinite"/>
  </rect>
</g>
<polygon points="29,13 32.5,22.4 42.6,22.7 34.6,29 37.4,38.8 29,33 20.6,38.8 23.4,29 15.4,22.7 25.5,22.4"
 fill="#ffffff" opacity="0.98" stroke="#a5340a" stroke-width="0.4"/>
</g></svg>`;
}

function pinDataUrl(job) {
    if (job.featured) {
        if (!_pinCache.__featured) {
            _pinCache.__featured = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(featuredGoldPinSvg())}`;
        }
        return _pinCache.__featured;
    }
    if (job.src === "employer") {
        if (!_pinCache.__employer) {
            _pinCache.__employer = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(employerPinSvg())}`;
        }
        return _pinCache.__employer;
    }
    const catId = categoryFor(job.cat).id;
    // Prefer the backend-provided logo_url; fall back to guessing a favicon from the company name.
    const logo = job.logo_url || faviconFor(job.company);
    if (logo) {
        const key = `l-${catId}-${logo}`;
        if (_pinCache[key]) return _pinCache[key];
        _pinCache[key] = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(logoPinSvg(catId, logo))}`;
        return _pinCache[key];
    }
    if (_pinCache[catId]) return _pinCache[catId];
    _pinCache[catId] = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(categoryPinSvg(catId))}`;
    return _pinCache[catId];
}

// Cluster renderer — orange gradient circle with white count.
const _clusterUrlCache = {};
function clusterDataUrl(count) {
    // Bucket by rough size so we don't create a unique SVG per number.
    const bucket = count < 10 ? "s" : count < 100 ? "m" : count < 1000 ? "l" : "xl";
    const key = `${bucket}-${count}`;
    if (_clusterUrlCache[key]) return _clusterUrlCache[key];
    const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="50" height="50" viewBox="0 0 50 50">
<defs><radialGradient id="g" cx="50%" cy="50%" r="50%">
<stop offset="0%" stop-color="#f47c3c"/><stop offset="100%" stop-color="#d94b08"/></radialGradient>
<filter id="s" x="-30%" y="-30%" width="160%" height="160%">
<feDropShadow dx="0" dy="4" stdDeviation="4" flood-color="#e85812" flood-opacity="0.45"/></filter></defs>
<circle cx="25" cy="25" r="18" fill="rgba(255,220,203,0.65)"/>
<circle cx="25" cy="25" r="14" fill="url(#g)" stroke="#ffffff" stroke-width="2" filter="url(#s)"/>
<text x="25" y="30" text-anchor="middle" fill="#ffffff" font-family="Outfit, DM Sans, system-ui, sans-serif" font-size="12" font-weight="700">${count}</text>
</svg>`;
    _clusterUrlCache[key] = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
    return _clusterUrlCache[key];
}

function renderCluster({ count, position }) {
    return new window.google.maps.Marker({
        position,
        icon: {
            url: clusterDataUrl(count),
            size: new window.google.maps.Size(50, 50),
            anchor: new window.google.maps.Point(25, 25),
            scaledSize: new window.google.maps.Size(50, 50),
        },
        zIndex: 100 + count,
    });
}

function escapeHtml(s) {
    return (s || "").toString().replace(/[&<>"']/g, (m) => (
        { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[m]
    ));
}

function clusterListHtml(jobs) {
    // Sort: featured (gold) first, then employer, then rest.
    const rank = (j) => (j.featured ? 0 : j.src === "employer" ? 1 : 2);
    const sorted = [...jobs].sort((a, b) => rank(a) - rank(b));
    const rows = sorted.slice(0, 20).map((j) => {
        const cat = categoryFor(j.cat);
        const stroke = CATEGORY_STROKE[cat.id] || CATEGORY_STROKE._default;
        const isEmp = j.src === "employer";
        const isGold = !!j.featured;
        const badge = isGold
            ? `<span style="background:linear-gradient(90deg,#fff3b8,#f5c542);color:#7a5806;font-size:9px;font-weight:800;padding:1px 6px;border-radius:6px;letter-spacing:.4px;text-transform:uppercase;margin-left:6px">★ Featured</span>`
            : isEmp
                ? `<span style="background:#e85812;color:#fff;font-size:9px;font-weight:700;padding:1px 5px;border-radius:6px;letter-spacing:.4px;text-transform:uppercase;margin-left:6px">★</span>`
                : "";
        const iconBg = isGold ? "#fff5cf" : isEmp ? "#ffdccb" : cat.color;
        const iconStroke = isGold ? "#b8890c" : isEmp ? "#e85812" : stroke;
        const rowLogo = !isGold && !isEmp ? (j.logo_url || faviconFor(j.company)) : "";
        const rowIcon = rowLogo
            ? `<img src="${escapeHtml(rowLogo)}" alt="" style="width:18px;height:18px;border-radius:5px;object-fit:cover" onerror="this.style.display='none';this.nextSibling&&(this.nextSibling.style.display='')"/><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="${iconStroke}" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="display:none">${CATEGORY_PATHS[cat.id] || CATEGORY_PATHS._default}</svg>`
            : `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="${iconStroke}" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">${CATEGORY_PATHS[cat.id] || CATEGORY_PATHS._default}</svg>`;
        return `<div class="dwj-cluster-row" data-jid="${escapeHtml(j.id)}"
            style="display:flex;align-items:center;gap:8px;padding:8px 10px;border-radius:10px;cursor:pointer;background:#fff;border:1px solid #f0f0f0;margin-bottom:6px;transition:background .15s">
          <span style="width:26px;height:26px;border-radius:8px;background:${iconBg};display:inline-flex;align-items:center;justify-content:center;shrink:0;overflow:hidden">
            ${rowIcon}
          </span>
          <div style="min-width:0;flex:1">
            <div style="font-family:'Outfit',sans-serif;font-weight:700;color:#1a1e2c;font-size:13px;line-height:1.2;white-space:nowrap;overflow:hidden;text-overflow:ellipsis">
              ${escapeHtml(j.title)}${badge}
            </div>
            <div style="color:#6b7080;font-size:11.5px;white-space:nowrap;overflow:hidden;text-overflow:ellipsis">${escapeHtml(j.company)}${j.pay ? ` · <span style="color:#e85812;font-weight:700">${escapeHtml(j.pay)}</span>` : ""}</div>
          </div>
        </div>`;
    }).join("");
    const more = jobs.length > 20 ? `<div style="text-align:center;font-size:11.5px;color:#6b7080;margin-top:4px">+${jobs.length - 20} more — zoom in further to see them all</div>` : "";
    return `<div style="min-width:260px;max-width:320px;font-family:'DM Sans',sans-serif;padding:2px">
      <div style="font-family:'Outfit',sans-serif;font-weight:800;font-size:14px;color:#1a1e2c;margin-bottom:8px;display:flex;align-items:center;gap:6px">
        <span style="background:#fff2ea;color:#e85812;font-size:10px;padding:2px 7px;border-radius:999px;font-weight:700;text-transform:uppercase;letter-spacing:.5px">${jobs.length} jobs here</span>
        <span style="color:#6b7080;font-weight:500;font-size:12px">Tap one to apply</span>
      </div>
      <div style="max-height:280px;overflow-y:auto;padding-right:2px">${rows}${more}</div>
    </div>`;
}

function popupHtml(job) {
    const cat = categoryFor(job.cat);
    const stroke = CATEGORY_STROKE[cat.id] || CATEGORY_STROKE._default;
    const isEmp = job.src === "employer";
    const isGold = !!job.featured;
    const iconBg = isGold ? "#fff5cf" : isEmp ? "#ffdccb" : cat.color;
    const iconStroke = isGold ? "#b8890c" : isEmp ? "#e85812" : stroke;
    const logo = !isGold && !isEmp ? (job.logo_url || faviconFor(job.company)) : "";
    const iconInner = logo
        ? `<img src="${escapeHtml(logo)}" alt="" style="width:22px;height:22px;border-radius:6px;object-fit:cover" onerror="this.style.display='none';this.nextSibling&&(this.nextSibling.style.display='')"/><svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="${iconStroke}" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="display:none">${CATEGORY_PATHS[cat.id] || CATEGORY_PATHS._default}</svg>`
        : `<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="${iconStroke}" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">${CATEGORY_PATHS[cat.id] || CATEGORY_PATHS._default}</svg>`;
    return `
    <div style="min-width:220px;font-family:'DM Sans',sans-serif;padding:2px">
      <div style="display:flex;align-items:center;gap:8px;margin-bottom:6px">
        <span style="width:30px;height:30px;border-radius:8px;background:${iconBg};display:inline-flex;align-items:center;justify-content:center;overflow:hidden">
          ${iconInner}
        </span>
        <strong style="font-family:'Outfit',sans-serif;color:#333A56;font-size:15px;line-height:1.15">${escapeHtml(job.title)}</strong>
      </div>
      ${isGold ? `<div style="display:inline-flex;align-items:center;gap:4px;background:linear-gradient(90deg,#fff3b8,#f5c542);color:#7a5806;font-size:11px;font-weight:800;padding:2px 9px;border-radius:999px;margin-bottom:4px;font-family:'Outfit',sans-serif;box-shadow:inset 0 -1px 0 rgba(184,137,12,0.35)">★ FEATURED</div>` : isEmp ? `<div style="display:inline-flex;align-items:center;gap:4px;background:#fff2ea;color:#e85812;font-size:11px;font-weight:700;padding:2px 8px;border-radius:999px;margin-bottom:4px;font-family:'Outfit',sans-serif">★ FEATURED EMPLOYER</div>` : ""}
      <div style="color:#4a536f;font-size:13px;margin-left:2px">${escapeHtml(job.company)}</div>
      <div style="color:#6b7080;font-size:12px;margin-top:2px;margin-left:2px">${escapeHtml(job.loc || job.city)}</div>
      ${job.pay ? `<div style="color:#e85812;font-weight:700;font-size:13px;margin-top:6px;font-family:'Outfit',sans-serif;margin-left:2px">${escapeHtml(job.pay)}</div>` : ""}
      <button data-jid="${escapeHtml(job.id)}" class="dwj-popup-apply" style="margin-top:10px;background:#e85812;color:#fff;border:none;padding:9px 14px;border-radius:999px;font-weight:600;font-family:'Outfit',sans-serif;cursor:pointer;width:100%;font-size:13px">1-tap Apply →</button>
    </div>`;
}

export default function MapView({ jobs, center, zoom, onJobClick, onBoundsChange, focusJobId }) {
    const mapEl = useRef(null);
    const mapRef = useRef(null);
    const clusterRef = useRef(null);
    const markersByIdRef = useRef({});
    const infoWindowRef = useRef(null);
    const jobsRef = useRef(jobs);
    const onJobClickRef = useRef(onJobClick);
    const [loadErr, setLoadErr] = useState("");
    const [mapReady, setMapReady] = useState(false);
    // Counter that increments on each map "idle" event — used purely as a
    // dependency to re-run the viewport-scoped marker sync (below).
    const [mapVersion, setMapVersion] = useState(0);

    useEffect(() => { jobsRef.current = jobs; }, [jobs]);
    useEffect(() => { onJobClickRef.current = onJobClick; }, [onJobClick]);

    // ---- Map init (runs once). ----
    useEffect(() => {
        let cancelled = false;
        window.gm_authFailure = () => {
            if (!cancelled) {
                setLoadErr(
                    "Google Maps API key was rejected. Add this domain to the allowed referrers in Google Cloud Console (or use an unrestricted key). Enable the 'Maps JavaScript API' too.",
                );
            }
        };
        loadGoogleMaps(GOOGLE_KEY)
            .then((google) => {
                if (cancelled || !mapEl.current) return;
                const map = new google.maps.Map(mapEl.current, {
                    center: { lat: 40, lng: -95 },
                    zoom: 4,
                    minZoom: 3,
                    maxZoom: 16,
                    disableDefaultUI: false,
                    zoomControl: true,
                    mapTypeControl: false,
                    streetViewControl: false,
                    fullscreenControl: false,
                    rotateControl: false,
                    scaleControl: false,
                    clickableIcons: false,
                    styles: LIGHT_STYLE,
                    restriction: { latLngBounds: NA_RESTRICTION, strictBounds: true },
                    zoomControlOptions: { position: google.maps.ControlPosition.RIGHT_BOTTOM },
                    gestureHandling: "greedy",
                    backgroundColor: "#eef1f5",
                });
                if (center && zoom) {
                    map.setCenter({ lat: center[0], lng: center[1] });
                    map.setZoom(zoom);
                } else {
                    map.fitBounds(DEFAULT_VIEW_BOUNDS);
                }
                mapRef.current = map;
                infoWindowRef.current = new google.maps.InfoWindow({
                    maxWidth: 260,
                    pixelOffset: new google.maps.Size(0, -6),
                });

                // Emit bounds on idle (for JobList side panel + mobile bbox refetch).
                let boundsTimer = null;
                const emit = () => {
                    try {
                        if (!mapRef.current) return;
                        const b = mapRef.current.getBounds();
                        if (!b) return;
                        const ne = b.getNorthEast();
                        const sw = b.getSouthWest();
                        const south = sw.lat(), west = sw.lng(), north = ne.lat(), east = ne.lng();
                        onBoundsChange && onBoundsChange({
                            contains: ([lat, lng]) =>
                                lat >= south && lat <= north &&
                                lng >= west && lng <= east,
                            bbox: { south, west, north, east },
                        });
                    } catch { /* map torn down */ }
                };
                map.addListener("idle", () => {
                    clearTimeout(boundsTimer);
                    boundsTimer = setTimeout(() => {
                        emit();
                        // Bump map version so the viewport-scoped marker sync effect re-runs.
                        setMapVersion((v) => v + 1);
                    }, 120);
                });
                // First emit slightly delayed so the map has actual bounds.
                setTimeout(emit, 250);

                setMapReady(true);
            })
            .catch((e) => {
                console.error("Google Maps load error:", e);
                setLoadErr(String(e.message || e));
            });
        return () => {
            cancelled = true;
            delete window.gm_authFailure;
            if (clusterRef.current) {
                try { clusterRef.current.clearMarkers(); } catch { /* ignore */ }
                clusterRef.current = null;
            }
            markersByIdRef.current = {};
            mapRef.current = null;
        };
    }, []);

    // ---- Build markers ONCE per jobs change; MarkerClusterer handles viewport. ----
    useEffect(() => {
        if (!mapReady || !mapRef.current || !window.google) return;
        const google = window.google;

        // ---- VIEWPORT-SCOPED MARKER LOADING ----
        // For large job sets, only create markers within the current viewport
        // (+50% padding buffer so pans don't flicker). Small sets skip filtering
        // to preserve the smoothest possible clustering.
        const VIEWPORT_MARKER_THRESHOLD = 300;
        let activeJobs = jobs;
        if (jobs.length > VIEWPORT_MARKER_THRESHOLD) {
            const b = mapRef.current.getBounds && mapRef.current.getBounds();
            if (b) {
                const ne = b.getNorthEast();
                const sw = b.getSouthWest();
                const latPad = (ne.lat() - sw.lat()) * 0.5;
                const lngPad = (ne.lng() - sw.lng()) * 0.5;
                const minLat = sw.lat() - latPad;
                const maxLat = ne.lat() + latPad;
                const minLng = sw.lng() - lngPad;
                const maxLng = ne.lng() + lngPad;
                activeJobs = jobs.filter((j) => {
                    if (j.lat == null || j.lng == null) return false;
                    return j.lat >= minLat && j.lat <= maxLat && j.lng >= minLng && j.lng <= maxLng;
                });
            }
        }

        // Diff: keep markers that still exist, add new ones, remove stale ones.
        const currentIds = new Set(activeJobs.map((j) => j.id));
        const prevIds = Object.keys(markersByIdRef.current);
        const toRemove = [];
        for (const id of prevIds) {
            if (!currentIds.has(id)) {
                toRemove.push(markersByIdRef.current[id]);
                delete markersByIdRef.current[id];
            }
        }
        if (clusterRef.current && toRemove.length) {
            try { clusterRef.current.removeMarkers(toRemove); } catch { /* ignore */ }
            toRemove.forEach((m) => m.setMap && m.setMap(null));
        }

        // Filter jobs that need new markers (dedup ids and require lat/lng).
        const newJobs = [];
        for (const j of activeJobs) {
            if (j.lat == null || j.lng == null) continue;
            if (markersByIdRef.current[j.id]) continue;
            newJobs.push(j);
        }

        // Pre-build icon objects per unique pin variant to avoid re-instantiating.
        const iconCache = {};
        const getIcon = (job) => {
            let key;
            if (job.featured) key = "__gold";
            else if (job.src === "employer") key = "__emp";
            else {
                const logo = job.logo_url || faviconFor(job.company);
                key = logo ? `l-${categoryFor(job.cat).id}-${logo}` : `c-${categoryFor(job.cat).id}`;
            }
            if (!iconCache[key]) {
                let w = 46, h = 56, ax = 23, ay = 54;
                if (job.featured) { w = 58; h = 70; ax = 29; ay = 68; }
                else if (job.src === "employer") { w = 54; h = 64; ax = 27; ay = 62; }
                iconCache[key] = {
                    url: pinDataUrl(job),
                    size: new google.maps.Size(w, h),
                    anchor: new google.maps.Point(ax, ay),
                    scaledSize: new google.maps.Size(w, h),
                };
            }
            return iconCache[key];
        };

        // Ensure clusterer exists (created once, reused across jobs updates).
        if (!clusterRef.current) {
            clusterRef.current = new MarkerClusterer({
                map: mapRef.current,
                markers: [],
                renderer: { render: renderCluster },
                onClusterClick: (event, cluster, map) => {
                    if (!map) return;
                    const gm = window.google && window.google.maps;
                    if (!gm) return;
                    const zoom = map.getZoom() || 0;
                    const maxZoom = 16;
                    // Gather jobs from cluster markers (fallback: look up by lat/lng if missing).
                    const clusterJobs = [];
                    for (const m of cluster.markers || []) {
                        if (m._dwjJob) {
                            clusterJobs.push(m._dwjJob);
                        }
                    }
                    // If we've reached max zoom OR all markers are within ~50m of each other,
                    // show the list popup instead of trying to zoom in more.
                    let sameSpot = true;
                    if (clusterJobs.length > 1) {
                        const first = clusterJobs[0];
                        for (const j of clusterJobs) {
                            if (Math.abs((j.lat || 0) - (first.lat || 0)) > 0.0008
                                || Math.abs((j.lng || 0) - (first.lng || 0)) > 0.0008) {
                                sameSpot = false;
                                break;
                            }
                        }
                    } else {
                        sameSpot = false;
                    }
                    if (zoom >= maxZoom - 1 || sameSpot) {
                        if (!infoWindowRef.current) return;
                        infoWindowRef.current.setContent(clusterListHtml(clusterJobs));
                        infoWindowRef.current.setPosition(cluster.position);
                        infoWindowRef.current.open({ map });
                        setTimeout(() => {
                            const nodes = document.querySelectorAll(".dwj-cluster-row");
                            nodes.forEach((el) => {
                                el.onmouseenter = () => { el.style.background = "#fff2ea"; };
                                el.onmouseleave = () => { el.style.background = "#fff"; };
                                el.onclick = () => {
                                    const jid = el.getAttribute("data-jid");
                                    const job = (jobsRef.current || []).find((x) => x.id === jid);
                                    if (job && onJobClickRef.current) {
                                        infoWindowRef.current && infoWindowRef.current.close();
                                        onJobClickRef.current(job);
                                    }
                                };
                            });
                        }, 60);
                    } else {
                        // Zoom in to fit the cluster bounds.
                        try {
                            if (cluster.bounds) {
                                map.fitBounds(cluster.bounds, 80);
                            } else {
                                map.panTo(cluster.position);
                                map.setZoom(Math.min(maxZoom, zoom + 2));
                            }
                        } catch {
                            map.panTo(cluster.position);
                            map.setZoom(Math.min(maxZoom, zoom + 2));
                        }
                    }
                },
            });
        }

        // Chunk-create markers with rAF so the UI stays responsive.
        let cancelled = false;
        const CHUNK = 300;
        let idx = 0;
        const build = () => {
            if (cancelled || !mapRef.current) return;
            const end = Math.min(idx + CHUNK, newJobs.length);
            const batch = [];
            for (; idx < end; idx++) {
                const j = newJobs[idx];
                const marker = new google.maps.Marker({
                    position: { lat: j.lat, lng: j.lng },
                    title: `${j.title} — ${j.company}`,
                    icon: getIcon(j),
                    // Featured (gold) pins need optimized:false so the SVG SMIL
                    // shimmer/halo animation renders — the default canvas-optimized
                    // path strips animations. Everyone else keeps optimized:true
                    // to preserve fast marker rendering at scale.
                    optimized: !j.featured,
                    zIndex: j.featured ? 1000 : j.src === "employer" ? 800 : 1,
                });
                // Attach job reference so the cluster click handler can enumerate members.
                marker._dwjJob = j;
                marker.addListener("click", () => {
                    if (!infoWindowRef.current || !mapRef.current) return;
                    const cur = (jobsRef.current || []).find((x) => x.id === j.id) || j;
                    infoWindowRef.current.setContent(popupHtml(cur));
                    infoWindowRef.current.open({ map: mapRef.current, anchor: marker });
                    setTimeout(() => {
                        const btn = document.querySelector(".dwj-popup-apply");
                        if (btn) {
                            btn.onclick = (e) => {
                                e.preventDefault();
                                const jid = btn.getAttribute("data-jid");
                                const job = (jobsRef.current || []).find((x) => x.id === jid) || cur;
                                if (onJobClickRef.current) onJobClickRef.current(job);
                            };
                        }
                    }, 60);
                });
                markersByIdRef.current[j.id] = marker;
                batch.push(marker);
            }
            if (batch.length && clusterRef.current) {
                try { clusterRef.current.addMarkers(batch, true); } catch { /* ignore */ }
            }
            if (idx < newJobs.length) {
                requestAnimationFrame(build);
            } else if (clusterRef.current) {
                // Trigger a single re-cluster at the end.
                try { clusterRef.current.render(); } catch { /* ignore */ }
            }
        };
        if (newJobs.length) requestAnimationFrame(build);

        return () => { cancelled = true; };
    }, [jobs, mapReady, mapVersion]);

    // ---- Focus a specific job (deep link). ----
    useEffect(() => {
        if (!focusJobId || !mapRef.current) return;
        const job = (jobs || []).find((j) => j.id === focusJobId);
        if (!job || job.lat == null) return;
        mapRef.current.panTo({ lat: job.lat, lng: job.lng });
        mapRef.current.setZoom(Math.max(mapRef.current.getZoom(), 10));
        setTimeout(() => {
            const m = markersByIdRef.current[job.id];
            if (m && window.google?.maps?.event) {
                window.google.maps.event.trigger(m, "click");
            }
        }, 700);
    }, [focusJobId, jobs]);

    // ---- Recenter when center prop changes (search / geolocate). ----
    useEffect(() => {
        if (!mapReady || !mapRef.current || !center || !window.google) return;
        const target = { lat: center[0], lng: center[1] };
        // Two-step: set zoom first, then center, so bounds calculations reflect final zoom.
        if (zoom) mapRef.current.setZoom(zoom);
        mapRef.current.setCenter(target);
        // Force a paint after Google's internal debouncing.
        setTimeout(() => {
            try {
                mapRef.current && mapRef.current.setCenter(target);
                const c = mapRef.current && mapRef.current.getCenter();
                console.log("[MapView] after setCenter, actual:", c && c.lat(), c && c.lng());
            } catch { /* ignore */ }
        }, 400);
    }, [mapReady, center, zoom]);

    return (
        <>
            <div
                ref={mapEl}
                data-testid="map-view"
                style={{ position: "absolute", inset: 0, zIndex: 0, background: "#eef1f5" }}
            />
            {loadErr && (
                <div className="absolute inset-0 flex items-center justify-center z-10 pointer-events-none">
                    <div className="glass px-5 py-3 rounded-2xl shadow-xl text-sm text-red-600 font-body max-w-md text-center pointer-events-auto">
                        Google Maps failed to load: {loadErr}
                        <div className="text-xs text-slate-500 mt-1">
                            Check that this domain is allowed for the API key in Google Cloud Console.
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}
